import {MasterType} from '@shared/enums/master-type.enum';
import {UniversalTable} from '@entities/components/universal-table/common.namespace';

/**
 * Auxiliary RightsTabsOption interface declaration.
 */
export interface RightsTabsOption {
	/** {string} name */
	name: string;
	/** {string} icon */
	icon: string;
}

/**
 * Auxiliary RightsSourcesOption interface declaration.
 */
export interface RightsSourcesOption {
	/** {string} name */
	name: string;
	/** {RightsFranchise | string} id */
	id: RightsFranchise | string;
}

/**
 * Auxiliary MasterMappingContent interface declaration.
 */
export interface MasterMappingContent {
	/** {number} id */
	id: number;
	/** {string} viewId */
	viewId: string;
	/** {MasterType} type */
	type: MasterType;
	/** {string} name */
	name: string;
	/** {number} percent */
	percent?: number;
}

/**
 * Auxiliary ChannelBrand interface declaration.
 */
export interface ChannelBrand {
	/** {number} id */
	id: number;
	/** {string} content */
	content: string;
}

/**
 * Auxiliary Inventory interface declaration.
 */
export interface Inventory {
	/** {number | string} assetId */
	assetId: number | string;
	/** {string} castAndCrew */
	castAndCrew: string;
	/** {string} channel */
	channel: string;
	/** {string} channelBrands */
	channelBrands: string;
	/** {string} content */
	content: string;
	/** {string} dateEnd */
	dateEnd: string;
	/** {number} dateEndUndefined */
	dateEndUndefined: number;
	/** {string} dateStart */
	dateStart: string;
	/** {string} specialDateStart */
	specialDateStart: string;
	/** {number} dateStartUndefined */
	dateStartUndefined: number;
	/** {number} isDateStartSpecial */
	isDateStartSpecial?: number;
	/** {string} dealId */
	dealId?: string;
	/** {string} dealName */
	dealName?: string;
	/** {number} durationMin */
	durationMin: number;
	/** {string} episodes */
	episodes: string;
	/** {string} exclusivity */
	exclusivity: string;
	/** {string} genreCode */
	genreCode: string;
	/** {string} id */
	id: string;
	/** {string} keywords */
	keywords: string;
	/** {string} language */
	language: string;
	/** {string} channels */
	channels: string;
	/** {string} genres */
	genres: string;
	/** {string} levelCodeInitial */
	levelCodeInitial: string;
	/** {MasterType} levelCode */
	levelCode: MasterType;
	/** {string} licensee */
	licensee?: string;
	/** {string} licensor */
	licensor?: string;
	/** {number | string} linkIdsAMCN */
	linkIdsAMCN: number | string;
	/** {number | string} linkIdsRL */
	linkIdsRL: number | string;
	/** {number | string} linkIdsRLJE */
	linkIdsRLJE: number | string;
	/** {string} media */
	media: string;
	/** {string} notes */
	notes: string;
	/** {number} numOfEpisodes */
	numOfEpisodes: number;
	/** {number} numOfSeason */
	numOfSeason: number;
	/** {string} productOwnerCode */
	productOwnerCode: string;
	/** {string} projectTypeCode */
	projectTypeCode: string;
	/** {string} rowNumOrder */
	rowNumOrder: string;
	/** {string} seasons */
	seasons: string;
	/** {string} shortSynopsys */
	shortSynopsys: string;
	/** {string} templateName */
	templateName: string;
	/** {string} territory */
	territory: string;
	/** {string} uniqueId */
	uniqueId: string; // self-created (UI)
	/** {string} flagTooltip */
	flagTooltip: string;
	/** {InventoryExtra[]} extra */
	extra?: InventoryExtra[];
	/** {string} mpaaRating */
	mpaaRating?: string;
	/** {string} tvRating */
	tvRating?: string;
	/** {string} releaseYear */
	releaseYear?: string;
}

/**
 * Auxiliary InventoryExtra interface declaration.
 */
export interface InventoryExtra {
	/** {string} inventoryAmcnId */
	inventoryAmcnId?: string;
	/** {string} inventoryTitle */
	inventoryTitle?: string;
	/** {string} sellOfEndDate */
	sellOfEndDate?: string;
	/** {string} sku */
	sku?: string;
	/** {string} upc */
	upc?: string;
	/** {string} elementCode */
	elementCode?: string;
	/** {string} elementName */
	elementName?: string;
}

/**
 * Auxiliary InventoryWithPagination interface declaration.
 */
export interface InventoryWithPagination {
	/** {Inventory[]} data */
	data: Inventory[];
	/** pagination */
	pagination: {
		pageNum: number;
		totalPages: number;
	};
}

/**
 * Auxiliary InventoryXLS interface declaration.
 */
export interface InventoryXLS extends Inventory {
	/** {Date} dateStartObject */
	dateStartObject: Date;
	/** {Date} dateEndObject */
	dateEndObject: Date;
}

/**
 * Auxiliary InventoryYear interface declaration.
 */
export interface InventoryYear {
	/** {string} start */
	start: string;
	/** {string} end */
	end: string;
}

/**
 * Auxiliary TimelineInventory interface declaration.
 */
export interface TimelineInventory extends Inventory, InventoryYear {
	/** {string | number} group */
	group: string | number;
	/** {string} style */
	style: string;
}

export type GroupedInventory = Record<string, Inventory[]>;

/**
 * Auxiliary InventorySorting interface declaration.
 */
export interface InventorySorting {
	/** {SortingFields} field */
	field: SortingFields;
	/** {UniversalTable.SortingOrder} order */
	order: UniversalTable.SortingOrder;
}

/**
 * Auxiliary FilterOption interface declaration.
 */
export interface FilterOption {
	/** {string} id */
	id: string;
	/** {string} name */
	name: string;
	/** {string} title */
	title: string;
	/** {number} level */
	level: number;
	/** {string} code */
	code?: string;
	/** {string} parentId */
	parentId?: string;
	/** {string} description */
	description?: string;
}

export interface RightsLanguageMetadata {
	LANGUAGE_ID: string;
	LANGUAGE: string;
	SUB: string;
	DUB: string;
	METADATA: string;
}

export type MediaType = FilterOption;
export type Territory = FilterOption;
export type Language = FilterOption;
export type Channel = FilterOption;
export type Genre = FilterOption;
export type ContentCode = FilterOption;
export type ProjectCode = FilterOption;
export type ProductCode = FilterOption;
export type WorkflowStatus = FilterOption;

/**
 * Auxiliary InventoryFilters interface declaration.
 */
export interface InventoryFilters {
	/** {MediaType['id'][]} mediaType */
	mediaType: MediaType['id'][];
	/** {Territory['id'][]} territory */
	territory: Territory['id'][];
	/** {Language['id'][]} language */
	language: Language['id'][];
	/** {Channel['id'][]} channel */
	channel: Channel['id'][];
	/** {Genre['id'][]} genre */
	genre: Genre['id'][];
	/** {ContentCode['id'][]} contentCode */
	contentCode: ContentCode['id'][];
	/** {ProjectCode['id'][]} projectCode */
	projectCode: ProjectCode['id'][];
	/** {string[]} projectCodeRequest */
	projectCodeRequest?: string[];
	/** {ProductCode['id'][]} productCode */
	productCode: ProductCode['id'][];
	/** {string[]} productCodeRequest */
	productCodeRequest?: string[];
	/** {WorkflowStatus['id'][]} workflowStatus */
	workflowStatus: WorkflowStatus['id'][];
	/** {string} text */
	text: string;
	/** {string} exclusivity */
	exclusivity?: string;
	/** {string} dateFrom */
	dateFrom?: string;
	/** {boolean} explicitMode */
	explicitMode?: boolean;
	// TODO: implement, remove "?"
	/** {any} dateTo */
	dateTo?: any;
}

export enum FilterType {
	MediaTypes = 'mediaTypes',
	Territories = 'territories',
	Languages = 'languages',
	Channels = 'channels',
	Genres = 'genres',
	ContentCodes = 'contentCodes',
	ProjectCodes = 'projectCodes',
	ProductCodes = 'productCodes',
	WorkflowStatuses = 'workflowStatuses',
}

/**
 * Auxiliary FiltersMap interface declaration.
 */
export interface FiltersMap {
	/** {Record<FilterOption['id'], Partial<FilterOption>>} [FilterType.MediaTypes] */
	[FilterType.MediaTypes]: Record<FilterOption['id'], Partial<FilterOption>>;
	/** {Record<FilterOption['id'], Partial<FilterOption>>} [FilterType.Territories] */
	[FilterType.Territories]: Record<FilterOption['id'], Partial<FilterOption>>;
	/** {Record<FilterOption['id'], Partial<FilterOption>>} [FilterType.Languages] */
	[FilterType.Languages]: Record<FilterOption['id'], Partial<FilterOption>>;
	/** {Record<FilterOption['id'], Partial<FilterOption>>} [FilterType.Channels] */
	[FilterType.Channels]: Record<FilterOption['id'], Partial<FilterOption>>;
	/** {Record<FilterOption['id'], Partial<FilterOption>>} [FilterType.Genres] */
	[FilterType.Genres]: Record<FilterOption['id'], Partial<FilterOption>>;
	/** {Record<FilterOption['id'], Partial<FilterOption>>} [FilterType.ContentCodes] */
	[FilterType.ContentCodes]: Record<FilterOption['id'], Partial<FilterOption>>;
	/** {Record<FilterOption['id'], Partial<FilterOption>>} [FilterType.ProjectCodes] */
	[FilterType.ProjectCodes]: Record<FilterOption['id'], Partial<FilterOption>>;
	/** {Record<FilterOption['id'], Partial<FilterOption>>} [FilterType.ProductCodes] */
	[FilterType.ProductCodes]: Record<FilterOption['id'], Partial<FilterOption>>;
	/** {Record<FilterOption['id'], Partial<FilterOption>>} [FilterType.WorkflowStatuses] */
	[FilterType.WorkflowStatuses]: Record<FilterOption['id'], Partial<FilterOption>>;
}

export enum View {
	Table = 'Table',
	Timeline = 'Timeline',
}

/**
 * Auxiliary ViewOption interface declaration.
 */
export interface ViewOption {
	/** {View} name */
	name: View;
	/** {string} icon */
	icon: string;
}

/**
 * Auxiliary ExclusivityOption interface declaration.
 */
export interface ExclusivityOption {
	/** {string} name */
	name: string;
	/** {string} id */
	id: string;
}

/**
 * Auxiliary RightsOption interface declaration.
 */
export interface RightsOption {
	/** {string} name */
	name: string;
	/** {RightsView} id */
	id: RightsView;
}

export type RightsInventoryBuffer = Map<string, Inventory[]>;

export enum GroupingMode {
	None = 'No grouping',
	Inventory = 'Inventory',
}

export enum Exclusivity {
	All = 'All',
	Exclusive = 'Exclusive',
	'Non-Exclusive' = 'Mixed Exclusivity',
}

export enum ExclusivityNames {
	all = 'All',
	exclusive = 'Exclusive',
	'non-exclusive' = 'Non-Exclusive',
}

export enum RightsView {
	Availability = 'AVAILABILITY',
	RightsIn = 'RIGHTS-IN',
	RightsOut = 'RIGHTS-OUT',
	Physical = 'PHYSICAL',
	Schedule = 'SCHEDULE',
}

/**
 * Auxiliary GroupOption interface declaration.
 */
export interface GroupOption {
	/** {GroupingMode} name */
	name: GroupingMode;
	/** {keyof Inventory} key */
	key?: keyof Inventory;
}

/**
 * Auxiliary InventoryGroup interface declaration.
 */
export interface InventoryGroup {
	/** {string} name */
	name: string;
	/** {keyof InventoryFilters} key */
	key: keyof InventoryFilters;
	/** {string} id */
	id: string;
}

/**
 * Auxiliary ModifyPinnedInventory interface declaration.
 */
export interface ModifyPinnedInventory {
	/** {string} pinnedKey */
	pinnedKey: string;
	/** {boolean} exclude */
	exclude: boolean;
	/** {string[]} listIds */
	listIds: string[];
}

export enum RightsFranchise {
	RLJE = 'RLJE',
	RLJE_STUDIO = 'RLJE_STUDIO',
	RL = 'RL',
	GOLDEN = 'GOLDEN',
	HIDIVE = 'HIDIVE',
}

export enum SortingFields {
	media = 'MEDIA',
	territory = 'TERRITORY',
	language = 'LANGUAGE',
	channels = 'CHANNELS',
	genres = 'GENRES',
	objectCode = 'OBJECT_CODE',
	objectName = 'OBJECT_NAME',
	channelBrand = 'CHANNEL_BRAND',
	actualTermStart = 'ACTUAL_TERM_START',
	actualTermEnd = 'ACTUAL_TERM_END',
	exclusivityValue = 'EXCLUSIVITY_VALUE',
	channelBrands = 'CHANNEL_BRANDS',
}

export enum RightsTablePatterns {
	MasterTypeConst = 'masterType',
	ExclusivityConst = 'exclusivity',
	Icon = 'icon',
}

/**
 * Auxiliary SelectedTreeItem interface declaration.
 */
export interface SelectedTreeItem {
	/** {Map<number, number>} multiSelectMap */
	multiSelectMap: Map<number, number>;
	/** {string} selectorRequester */
	selectorRequester: string;
	/** {boolean} isAllUnchecked */
	isAllUnchecked?: boolean;
}
